<script lang="ts" setup>
  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import SelectButton from 'primevue/selectbutton'
  import EmployeeStateIcon from '@/elements/EmployeeStateIcon.vue'
  import { useAuthStore, useGeneratedEmployeeStateStore, useGeneratedEmployeeStore } from '@/stores'
  import { apiWorktimeTrackingSummaryGet, apiWorktimeTrackingSummaryPost, type EmployeeStateJsonldDefaultReadSync } from '@/api'

  import WidgetSkeleton from '@/components/dashboard/WidgetSkeleton.vue'
  import InputDateTimePicker from '@/elements/InputDateTimePicker.vue'
  import DialogFitContent from '@/components/DialogFitContent.vue'
  import { useTitrop } from '@/composables/useTitrop.ts'
  import { DateTime } from 'luxon'
  import { useEventBus } from '@vueuse/core'
  import ButtonDefault from '@/elements/ButtonDefault.vue'

  const titrop = useTitrop()
  const { t } = useI18n()

  const isLoading = ref(true)
  const empStates = ref<Record<string, EmployeeStateJsonldDefaultReadSync>>()

  const availableStates = computed(() => (empStates.value ? Object.values(empStates.value).filter((state) => state['@id'] != titrop.value?.user?.current.current_state) : []))
  onMounted(async () => {
    empStates.value = await useGeneratedEmployeeStateStore()
      .findAllAsIriDictionary(null)
      .then((d) => d.value)
    isLoading.value = false
  })

  async function quickSaveOrJump(newVal: string) {
    if (!titrop.value.user.current || !empStates.value) return
    dialogCurrentState.value = newVal ?? dialogCurrentState.value ?? titrop.value.user.current.current_state
    if (empStates.value[dialogCurrentState.value].working) {
      isLoading.value = true

      await useGeneratedEmployeeStore().putId({
        path: { id: titrop.value.user.current['@id'] },
        body: {
          current_state: dialogCurrentState.value,
          current_state_note: '',
          current_state_until: null
        }
      })
      await useAuthStore().refreshWhoAmI()
      dialogCurrentStateNote.value = titrop.value.user.current.current_state_note ?? undefined
      dialogCurrentStateUntil.value = titrop.value.user.current.current_state_until ? DateTime.fromISO(titrop.value.user.current.current_state_until) : null

      isLoading.value = false
    } else {
      dialogCurrentState.value = newVal
      dialogCurrentStateNote.value = titrop.value.user.current.current_state_note ?? undefined
      dialogCurrentStateUntil.value = titrop.value.user.current.current_state_until ? DateTime.fromISO(titrop.value.user.current.current_state_until) : null
      dialogOpen.value = true
    }
  }

  const dialogCurrentState = ref<string | null>()
  const dialogCurrentStateNote = ref<string>()
  const dialogCurrentStateUntil = ref<DateTime | null>()
  const dialogOpen = ref(false)

  async function onDialogSaveClicked() {
    isLoading.value = true
    await useGeneratedEmployeeStore().putId({
      path: { id: String(titrop.value.user.currentId) },
      body: {
        current_state: dialogCurrentState.value ?? undefined,
        current_state_note: dialogCurrentStateNote.value,
        current_state_until: dialogCurrentStateUntil.value?.toISO()
      }
    })
    if (['Urlaub', 'Krank'].includes(empStates.value?.[dialogCurrentState.value!]?.title!)) {
      const daySummary = (
        await apiWorktimeTrackingSummaryGet<true>({
          query: {
            date: DateTime.now().toISO({ includeOffset: false })
          }
        })
      ).data
      if (daySummary.actions?.includes('WORK_END')) {
        await apiWorktimeTrackingSummaryPost<true>({ body: { action: 'WORK_END' } })
        useEventBus('state_change_and_day_end_post').emit()
      }
    }
    await useAuthStore().refreshWhoAmI()
    dialogCurrentStateNote.value = titrop.value.user.current.current_state_note ?? undefined
    dialogCurrentStateUntil.value = titrop.value.user.current.current_state_until ? DateTime.fromISO(titrop.value.user.current.current_state_until) : null
    isLoading.value = false
    dialogOpen.value = false
  }

  function onDialogClose() {
    dialogCurrentState.value = undefined
  }

  useEventBus('work_begin_post').on(async () => {
    await useAuthStore().refreshWhoAmI()
  })

  const currentState = computed(() => {
    return empStates.value?.[titrop.value?.user?.current.current_state!]
  })

  const vacationState = '/api/employee_states/4'
  const isVacationState = computed(() => dialogCurrentState.value === vacationState)
</script>

<template>
  <div class="max-w-full @container">
    <WidgetSkeleton v-if="isLoading || !$titrop?.user?.current || !$titrop?.user?.current.current_state || !empStates" variant="state" />
    <div
      v-else
      class="sm:rounded-lg text-sm">
      <div class="text-center justify-center flex flex-row items-center gap-x-2 @sm:block">
        <span>{{ t('current') }}</span>
        <div class="flex justify-center items-center text-sm">
          <div class="flex flex-row @sm:flex-col justify-center items-center @sm:border @sm:shadow @sm:aspect-square @sm:rounded-full @sm:px-4">
            <EmployeeStateIcon
              :code="currentState?.code!"
              class="size-5 @sm:size-10 mr-1 @sm:mr-0" />
            <span>{{ currentState?.title! }}</span>
          </div>
        </div>
      </div>
      <Divider class="!my-2.5" />
      <div>
        <SelectButton
          v-model="dialogCurrentState"
          :options="availableStates"
          :pt:pcToggleButton:root="'border flex justify-center @xs:grow w-full'"
          aria-labelledby="multiple"
          class="flex flex-col @md:flex-row justify-center"
          option-label="title"
          option-value="@id"
          @update:model-value="quickSaveOrJump">
          <template #option="slotProps">
            <span class="flex flex-row @md:flex-col items-center">
              <EmployeeStateIcon
                :code="slotProps.option.code"
                class="size-5 @md:size-10 mr-1 @sm:mr-0" />
              <span class="text-xs leading-6">{{ slotProps.option.title }}</span>
            </span>
          </template>
        </SelectButton>
      </div>
    </div>

    <DialogFitContent
      v-model:open-state="dialogOpen"
      :no-padding="false"
      :title="t('dialogTitle', { name: $titrop.user.current?.displayname })"
      @close="onDialogClose">
      <template #content>
        <div class="sm:rounded-lg mt-2">
          <div>
            <SelectButton
              v-model="dialogCurrentState"
              :options="availableStates"
              aria-labelledby="multiple"
              option-label="title"
              option-value="@id"
              class="w-full">
              <template #option="slotProps">
                <span class="flex flex-col items-center">
                  <EmployeeStateIcon :code="slotProps.option.code" />
                  <span class="text-xs leading-6">{{ slotProps.option.title }}</span>
                </span>
              </template>
            </SelectButton>
          </div>

          <div v-if="!(empStates && dialogCurrentState && (empStates[dialogCurrentState]?.working ?? true))"
               class="mt-4">
            <FloatLabel variant="on">
              <label
                for="trackinginfo">
                Optionale Bemerkung
              </label>
              <InputText
                id="StateDescription"
                v-model="dialogCurrentStateNote"
                name="StateDescription"
                class="block w-full resize-none rounded-md py-1.5 shadow-sm sm:text-sm sm:leading-6" />
            </FloatLabel>
          </div>

          <div v-if="!(empStates && dialogCurrentState && (empStates[dialogCurrentState]?.working ?? true))"
               class="mt-4">
            <FloatLabel variant="on">
              <label
                for="DateTo">
                {{ t('until') }}
              </label>
              <InputDateTimePicker
                id="DateTo"
                v-model="dialogCurrentStateUntil"
                class="p-inputwrapper-filled"
                name="DateTo"
                type="date"
                :invalid="isVacationState && !dialogCurrentStateUntil" />
            </FloatLabel>
            <Message v-if="isVacationState && !dialogCurrentStateUntil" size="small" severity="warn">
              {{ t('vacationEndRequired') }}
            </Message>
          </div>

          <div class="mt-4 w-full flex justify-end">
            <ButtonDefault
              type="button"
              @click="onDialogSaveClicked"
              :label="t('save')"
              icon="SaveIcon"
              :disabled="isVacationState && !dialogCurrentStateUntil">
            </ButtonDefault>
          </div>
        </div>
      </template>
    </DialogFitContent>
  </div>
</template>

<style scoped></style>

<i18n>
{
  "de": {
    "current": "Aktuell:",
    "dialogTitle": "Statusänderung für {name}",
    "until": "Bis",
    "vacationEndRequired": "Das Enddatum für den Urlaub muss angegeben werden",
    "save": "Speichern"
  },
  "en": {
    "current": "Current:",
    "dialogTitle": "Status change for {name}",
    "until": "Until",
    "vacationEndRequired": "An end date is required for vacation",
    "save": "Save"
  }
}
</i18n>
